import { motion } from "framer-motion";
import { CheckCircle2, Target, Eye, MapPin, Linkedin } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { mediaUrl } from "@/lib/media";
import { Section, SectionTitle } from "@/components/Section";

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

function TeamSection() {
  const { settings } = useSettings();
  const [team, setTeam] = useState(null);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => setTeam([]));
  }, []);

  if (team === null) return null;
  if (team.length === 0) return null;  // hide entirely when no members

  return (
    <section className="bg-[#FAFAFA] py-20 relative overflow-hidden" data-testid="about-team">
      <div className="absolute inset-0 grid-bg opacity-15 pointer-events-none" />
      <Section className="relative">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="label-eyebrow">{settings?.team_eyebrow || "MANAGEMENT TEAM"}</div>
          <h2 className="heading-xl text-3xl sm:text-4xl lg:text-5xl mt-4">
            {settings?.team_title || "MEET THE PEOPLE BEHIND TARA."}
          </h2>
          {settings?.team_subtitle && (
            <p className="mt-5 text-gray-700 text-base sm:text-lg leading-relaxed">{settings.team_subtitle}</p>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 max-w-6xl mx-auto">
          {team.map((m, i) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
              className="text-center group"
              data-testid={`team-member-${m.id}`}
            >
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-[#E65C00] translate-x-3 translate-y-3 transition-transform group-hover:translate-x-1 group-hover:translate-y-1 rounded-full" aria-hidden />
                <div className="relative w-44 h-44 sm:w-48 sm:h-48 rounded-full overflow-hidden border-4 border-[#1A1A1A] bg-[#1A1A1A]">
                  {m.photo ? (
                    <img
                      src={mediaUrl(m.photo)}
                      alt={m.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-heading font-black text-5xl text-[#E65C00]">
                      {m.name?.[0] || "T"}
                    </div>
                  )}
                </div>
              </div>
              <h3 className="font-heading font-black text-xl uppercase mt-6 text-[#1A1A1A]">{m.name}</h3>
              <div className="text-xs uppercase tracking-[0.25em] text-[#E65C00] font-bold mt-1">{m.role}</div>
              {m.bio && <p className="text-sm text-gray-700 mt-3 leading-relaxed px-2">{m.bio}</p>}
              {m.linkedin && (
                <a
                  href={m.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex mt-3 w-9 h-9 items-center justify-center bg-[#1A1A1A] text-[#FFCC00] hover:bg-[#E65C00] hover:text-white transition-colors"
                  aria-label={`${m.name} on LinkedIn`}
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </Section>
    </section>
  );
}

export default function About() {
  const { settings } = useSettings();
  return (
    <>
      <section className="relative bg-[#1A1A1A] text-white py-24 overflow-hidden noise-overlay" data-testid="about-hero">
        {settings?.about_hero_image && (
          <>
            <img src={mediaUrl(settings.about_hero_image)} alt="" className="absolute inset-0 w-full h-full object-cover object-center opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/95 via-[#1A1A1A]/70 to-[#1A1A1A]/30" />
          </>
        )}
        <div className="absolute inset-0 grid-bg opacity-20" />
        <Section className="relative">
          <div className="label-eyebrow mb-4">About Us</div>
          <h1 className="heading-xl text-3xl sm:text-4xl lg:text-5xl text-white max-w-4xl">
            BUSINESS WITH US MEANS<br /><span className="text-[#E65C00]">"PARTNERING TRUST."</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-gray-300 max-w-2xl">
            Tara Autohub Pvt. Ltd. was incorporated in April 2016 with a vision to transform the construction equipment industry in Bihar, India.
          </p>
        </Section>
      </section>

      <Section className="py-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fadeUp}>
          {settings?.about_story_image ? (
            <img src={mediaUrl(settings.about_story_image)} alt="Tara Autohub" className="w-full h-[500px] object-cover" />
          ) : (
            <div className="w-full h-[500px] bg-[#1A1A1A] grid-bg flex items-center justify-center" data-testid="about-story-placeholder">
              <div className="text-center px-6">
                <div className="font-heading font-black text-5xl text-[#E65C00] opacity-30">OUR STORY</div>
                <div className="text-xs uppercase tracking-[0.3em] text-gray-500 mt-3">Upload an image in<br />Admin → Settings → About Page</div>
              </div>
            </div>
          )}
        </motion.div>
        <motion.div {...fadeUp}>
          <SectionTitle eyebrow={settings?.about_story_eyebrow || "Our Story"} title={settings?.about_story_title || "A ONE-STOP SOLUTION."} />
          <div
            className="mt-6 text-gray-700 leading-relaxed prose prose-sm max-w-none [&>p]:mt-4 [&>p:first-child]:mt-0"
            data-testid="about-story-text"
            dangerouslySetInnerHTML={{
              __html: settings?.about_story_text || `<p>Tara Autohub Pvt. Ltd. was incorporated in April 2016 by a team of young and dynamic entrepreneurs.</p>`,
            }}
          />
        </motion.div>
      </Section>

      {/* Timeline — admin-editable, hides if empty */}
      {settings?.about_timeline_items && settings.about_timeline_items.length > 0 && (
        <section className="bg-[#0F0F0F] text-white py-20 relative overflow-hidden noise-overlay" data-testid="about-timeline">
          <div className="absolute inset-0 grid-bg opacity-15 pointer-events-none" />
          <Section className="relative">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="label-eyebrow !text-[#FFCC00]">{settings?.about_timeline_eyebrow || "Our Journey"}</div>
              <h2 className="heading-xl text-3xl sm:text-4xl lg:text-5xl mt-4 text-white">
                {settings?.about_timeline_title || "MILESTONES THAT BUILT US."}
              </h2>
            </div>
            <div className="relative max-w-3xl mx-auto">
              {/* Vertical line */}
              <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#E65C00] via-[#FFCC00] to-[#E65C00]" />
              <div className="space-y-12">
                {settings.about_timeline_items.map((m, i) => {
                  const left = i % 2 === 0;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: left ? -40 : 40 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.6, delay: i * 0.05 }}
                      className={`relative pl-12 sm:pl-0 sm:flex ${left ? "sm:justify-end" : ""}`}
                      data-testid={`timeline-item-${i}`}
                    >
                      {/* Dot */}
                      <div className="absolute left-4 sm:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#E65C00] border-4 border-[#0F0F0F] z-10" />
                      <div className={`sm:w-[44%] bg-[#1A1A1A] border-l-4 border-[#E65C00] p-5 ${left ? "sm:mr-[6%]" : "sm:ml-[6%]"}`}>
                        <div className="font-heading font-black text-3xl text-[#FFCC00]">{m.year}</div>
                        <div className="font-heading font-black uppercase text-base mt-2 text-white">{m.title}</div>
                        {m.desc && <p className="text-sm text-gray-400 mt-2 leading-relaxed">{m.desc}</p>}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </Section>
        </section>
      )}

      {/* Management Team — admin-driven, hides if empty */}
      <TeamSection />

      <section className="bg-gray-50 py-20" data-testid="about-mvk">
        <Section>
          <div className="grid md:grid-cols-3 gap-8">
            {(settings?.about_mvk_items && settings.about_mvk_items.length > 0
              ? settings.about_mvk_items.map((it, i) => ({ ...it, icon: [Target, Eye, MapPin][i % 3] }))
              : [
                  { icon: Target, title: "Our Mission", desc: "To became one stop service provider for construction equipment & commercial vehicles." },
                  { icon: Eye, title: "Our Vision", desc: "To became the most trusted service provider to our client base on PAN INDIA level." },
                  { icon: MapPin, title: "Key Market", desc: "Construction & mining industries. Railway to Roads, Rivers to High-rises." },
                ]
            ).map((b, i) => (
              <motion.div key={`${b.title}-${i}`} {...fadeUp} className="bg-white p-8 border border-gray-200 hover:border-[#E65C00] transition-colors" data-testid={`about-card-${b.title}`}>
                <b.icon className="w-8 h-8 text-[#E65C00]" />
                <h3 className="heading-xl text-xl mt-5 uppercase">{b.title}</h3>
                <p className="text-gray-700 leading-relaxed mt-3 text-sm">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </section>

      <Section className="py-20">
        <SectionTitle eyebrow={settings?.about_edge_eyebrow || "Why Choose Us"} title={settings?.about_edge_title || "THE TARA EDGE."} />
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(settings?.about_edge_bullets && settings.about_edge_bullets.length > 0
            ? settings.about_edge_bullets
            : [
                "Authorised dealer for Bull Machines across Bihar",
                "Multi-brand inventory: JCB, Tata Hitachi, Volvo, BEML, CAT",
                "87-point inspection on every used machine",
                "In-house finance desk with 8+ NBFC tie-ups",
                "On-site service team across Patna, Begusarai, Purnia",
                "Best resale value commitment for used equipment",
              ]
          ).map((p) => (
            <div key={p} className="flex items-start gap-3 p-5 border border-gray-200 hover:border-[#1A1A1A] transition-colors">
              <CheckCircle2 className="w-5 h-5 text-[#E65C00] mt-0.5 shrink-0" />
              <span className="text-sm font-medium text-gray-800">{p}</span>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}
